import type { QueryClient } from '@tanstack/react-query'

import { qk } from '@/api/query-keys'
import type {
  Application,
  AuthenticatedUser,
  GlobalPermissions,
  Job,
  UserWorkspace,
  WorkspaceUser,
} from '@/api/types'
import { getSession } from '@/features/auth/session-store'
import type { RealtimeClient, RealtimeContext } from '../client'
import type { RealtimeMessage } from '../protocol'
import * as apps from '@/features/application/cache'
import { addJob } from './jobs'
import * as notifications from './notifications'
import * as ws from '@/features/workspace/cache'

/**
 * The core realtime handlers: workspaces, their members, and applications.
 *
 * Every one of these patches the query cache with `setQueryData` rather than
 * invalidating it. That is the whole point of the websocket -- a rename arriving
 * from another tab should repaint the name, not provoke a round trip per event
 * for every connected client of the workspace.
 *
 * Ported from `registerCoreEvents` in
 * `web-frontend/modules/core/plugins/realTimeHandler.js`.
 *
 * The notification list is the one exception, and it is invalidated rather than
 * patched. Two reasons, and the second is why it is not a compromise. Its shape
 * is a paginated read that M5 owns and this commit would be inventing. And an
 * invalidation only refetches a query something is currently subscribed to, so
 * with the notification panel closed -- which is nearly always -- it costs one
 * cache flag and no request at all. The unread counts, which are on screen the
 * whole time, are patched: the server puts each workspace's count on the
 * workspace itself, so they are already in a cache this file owns.
 */

/**
 * What `user_data_updated` carries.
 *
 * The three registered `UserDataType`s, all optional because the server sends
 * only what changed: `permissions` and `user_notifications`, both registered in
 * `baserow/api` under `user_data_types.py`, plus the user themselves.
 */
interface UserDataPayload {
  user?: Partial<AuthenticatedUser>
  permissions?: GlobalPermissions[]
  user_notifications?: { unread_count: number }
}

type WorkspaceUpdater = (workspaces: UserWorkspace[]) => UserWorkspace[]
type ApplicationUpdater = (applications: Application[]) => Application[]

function patchWorkspaces(queryClient: QueryClient, update: WorkspaceUpdater): void {
  queryClient.setQueryData<UserWorkspace[]>(qk.workspaces(), (current) =>
    current === undefined ? current : update(current),
  )
}

function patchApplications(
  queryClient: QueryClient,
  workspaceId: number,
  update: ApplicationUpdater,
): void {
  queryClient.setQueryData<Application[]>(qk.applications(workspaceId), (current) =>
    current === undefined ? current : update(current),
  )
}

/**
 * Applications are cached per workspace, but most application events name only
 * the application. Rather than keep a second index, this walks the cached lists
 * -- there is one per workspace the user has opened, so a handful at most.
 */
function patchApplicationById(
  queryClient: QueryClient,
  applicationId: number,
  update: ApplicationUpdater,
): void {
  const entries = queryClient.getQueriesData<Application[]>({
    queryKey: [...qk.all, 'applications'],
  })

  for (const [key, applications] of entries) {
    if (applications === undefined) continue
    if (!applications.some((application) => application.id === applicationId)) continue
    queryClient.setQueryData<Application[]>(key, update(applications))
  }
}

export function registerCoreHandlers(client: RealtimeClient): () => void {
  const off: (() => void)[] = []
  const on = (
    type: string,
    handler: (c: RealtimeContext, d: RealtimeMessage) => void,
  ) => {
    off.push(client.on(type, handler))
  }

  /* ------------------------------------------------------------------ user */

  on('user_data_updated', (_context, data) => {
    const payload = data.user_data as UserDataPayload | undefined
    if (payload === undefined) return

    const session = getSession()
    if (payload.user !== undefined) session.updateUser(payload.user)
    if (payload.permissions !== undefined) session.setPermissions(payload.permissions)
    if (payload.user_notifications !== undefined) {
      session.setUnreadNotificationCount(payload.user_notifications.unread_count)
    }
  })

  on('user_updated', ({ queryClient }, data) => {
    const user = data.user as { id: number; first_name: string }
    // `first_name` on the wire, `name` on a WorkspaceUser. Upstream maps it here
    // rather than at the edge, and the shapes really do differ.
    patchWorkspaces(queryClient, (workspaces) =>
      ws.updateUserEverywhere(workspaces, user.id, { name: user.first_name }),
    )
  })

  /* ------------------------------------------------------------ workspaces */

  // `group_*` on the wire: the rename to "workspace" never reached the protocol.
  on('group_created', ({ queryClient }, data) => {
    patchWorkspaces(queryClient, (workspaces) =>
      ws.addWorkspace(workspaces, data.workspace as UserWorkspace),
    )
  })

  on('group_restored', ({ queryClient }, data) => {
    const workspace = data.workspace as UserWorkspace
    patchWorkspaces(queryClient, (workspaces) => ws.addWorkspace(workspaces, workspace))

    // A restore brings back every application that went into the trash with it.
    const applications = (data.applications ?? []) as Application[]
    patchApplications(queryClient, workspace.id, (current) =>
      apps.addApplications(current, applications),
    )
  })

  on('group_updated', ({ queryClient }, data) => {
    patchWorkspaces(queryClient, (workspaces) =>
      ws.updateWorkspace(
        workspaces,
        data.workspace_id as number,
        data.workspace as Partial<UserWorkspace>,
      ),
    )
  })

  on('group_deleted', ({ queryClient }, data) => {
    const workspaceId = data.workspace_id as number
    patchWorkspaces(queryClient, (workspaces) =>
      ws.removeWorkspace(workspaces, workspaceId),
    )
    // The applications went with it. Removed rather than invalidated: refetching
    // a workspace the user has just lost access to answers 404.
    queryClient.removeQueries({ queryKey: qk.applications(workspaceId) })
  })

  /* ------------------------------------------------------- workspace users */

  on('group_user_added', ({ queryClient }, data) => {
    patchWorkspaces(queryClient, (workspaces) =>
      ws.addWorkspaceUser(
        workspaces,
        data.workspace_id as number,
        data.workspace_user as WorkspaceUser,
      ),
    )
  })

  on('group_user_updated', ({ queryClient }, data) => {
    const workspaceId = data.workspace_id as number
    const values = data.workspace_user as Partial<WorkspaceUser>

    patchWorkspaces(queryClient, (workspaces) => {
      const withUser = ws.updateWorkspaceUser(
        workspaces,
        workspaceId,
        data.id as number,
        values,
      )
      // When it is this user's own membership that changed, their permissions
      // for the workspace changed with it, and those live on the workspace as
      // well as on the membership.
      if (values.user_id !== getSession().user?.id) return withUser
      return ws.updateWorkspace(withUser, workspaceId, {
        permissions: values.permissions,
      } as Partial<UserWorkspace>)
    })
  })

  on('group_user_deleted', ({ queryClient }, data) => {
    const workspaceId = data.workspace_id as number
    const values = data.workspace_user as Partial<WorkspaceUser>

    // Being removed from a workspace removes the workspace, not a row in its
    // member list -- there is no longer a workspace here to look at.
    if (values.user_id === getSession().user?.id) {
      patchWorkspaces(queryClient, (workspaces) =>
        ws.removeWorkspace(workspaces, workspaceId),
      )
      queryClient.removeQueries({ queryKey: qk.applications(workspaceId) })
      return
    }

    patchWorkspaces(queryClient, (workspaces) =>
      ws.removeWorkspaceUser(workspaces, workspaceId, data.id as number),
    )
  })

  /* ---------------------------------------------------------- applications */

  on('application_created', ({ queryClient }, data) => {
    const application = data.application as Application
    patchApplications(queryClient, application.workspace.id, (applications) =>
      apps.addApplication(applications, application),
    )
  })

  on('application_updated', ({ queryClient }, data) => {
    const applicationId = data.application_id as number
    const values = data.application as Partial<Application>
    patchApplicationById(queryClient, applicationId, (applications) =>
      apps.updateApplication(applications, applicationId, values),
    )
  })

  on('application_deleted', ({ queryClient }, data) => {
    const applicationId = data.application_id as number
    patchApplicationById(queryClient, applicationId, (applications) =>
      apps.removeApplication(applications, applicationId),
    )
  })

  on('applications_reordered', ({ queryClient }, data) => {
    patchApplications(queryClient, data.workspace_id as number, (applications) =>
      apps.orderApplications(applications, data.order as string[]),
    )
  })

  /* -------------------------------------------------------- notifications */

  on('notifications_created', ({ queryClient }, data) => {
    const incoming = data.notifications as notifications.IncomingNotification[]
    const deltas = notifications.unreadDeltasFor(incoming)

    patchWorkspaces(queryClient, (workspaces) =>
      notifications.applyUnreadDeltas(workspaces, deltas),
    )
    bumpUserUnread(notifications.userUnreadDelta(deltas))
    invalidateNotifications(queryClient, deltas)
  })

  on('notifications_fetch_required', ({ queryClient }, data) => {
    // The server counted but did not send: too many arrived at once to be worth
    // pushing. The counts are the payload; the list has to be refetched.
    const deltas = (data.notifications_added ?? []) as notifications.UnreadDelta[]

    patchWorkspaces(queryClient, (workspaces) =>
      notifications.applyUnreadDeltas(workspaces, deltas),
    )
    bumpUserUnread(notifications.userUnreadDelta(deltas))
    invalidateNotifications(queryClient, deltas)
  })

  on('notification_marked_as_read', ({ queryClient }, data) => {
    const notification = data.notification as notifications.IncomingNotification
    const workspaceId = notification.workspace?.id ?? null

    if (workspaceId === null) {
      bumpUserUnread(-1)
    } else {
      patchWorkspaces(queryClient, (workspaces) =>
        notifications.changeWorkspaceUnread(workspaces, workspaceId, -1),
      )
    }
    invalidateNotifications(queryClient, [{ workspace_id: workspaceId, count: 0 }])
  })

  on('all_notifications_marked_as_read', ({ queryClient }) => {
    clearEveryUnreadCount(queryClient)
  })

  on('all_notifications_cleared', ({ queryClient }) => {
    clearEveryUnreadCount(queryClient)
  })

  /* ----------------------------------------------------------------- jobs */

  on('job_started', ({ queryClient }, data) => {
    // Appended rather than invalidated, because a job list that is polling has
    // to know immediately that there is something to poll for. Upstream also
    // restarts its polling timer here; there is nothing to restart in a query
    // whose refetch interval is derived from whether any job is unfinished.
    queryClient.setQueryData<Job[]>(qk.jobs(), (current) =>
      current === undefined ? current : addJob(current, data.job as Job),
    )
  })

  return () => {
    for (const remove of off) remove()
  }
}

function bumpUserUnread(delta: number): void {
  if (delta === 0) return
  const session = getSession()
  session.setUnreadNotificationCount(session.unreadNotificationCount + delta)
}

/**
 * Marks the affected notification lists stale.
 *
 * A delta with no workspace is a notification addressed to the user, which the
 * panel shows whichever workspace is open, so every list has to be marked.
 */
function invalidateNotifications(
  queryClient: QueryClient,
  deltas: notifications.UnreadDelta[],
): void {
  if (deltas.some((delta) => delta.workspace_id === null)) {
    void queryClient.invalidateQueries({ queryKey: [...qk.all, 'notifications'] })
    return
  }

  for (const delta of deltas) {
    void queryClient.invalidateQueries({
      queryKey: qk.notifications(delta.workspace_id!),
    })
  }
}

/**
 * Zeroes every unread count and marks every notification list stale.
 *
 * `all_notifications_marked_as_read` and `all_notifications_cleared` are both
 * workspace-scoped on the server -- `clear_all_notifications(user, workspace)`
 * -- but neither payload carries the workspace. So the receiving client cannot
 * tell which one was cleared, and zeroing only the workspace on screen, as the
 * Vue app does, is wrong whenever the action came from another tab looking at a
 * different one. Zeroing all of them is wrong in the other direction, so the
 * refetch is what settles it; the counts are cleared meanwhile to keep the
 * badge from claiming unread notifications that have just been read.
 */
/**
 * What another tab of this user's did to the whole of one workspace.
 *
 * These two events reach only the user's *other* sessions -- the tab that acted
 * is excluded from the broadcast and updates itself precisely -- and they carry
 * no workspace, so this tab cannot know which one was affected.
 *
 * The user-level count is still exact: both endpoints filter
 * `workspace_id = <workspace> OR workspace_id IS NULL`, so the notifications
 * that belong to the user rather than to any workspace are always included.
 *
 * The per-workspace counts are not guessed. Zeroing them all would be wrong --
 * marking one workspace's notifications read says nothing about another's -- and
 * guessing then correcting on the refetch would put every badge through a
 * visible flash of zero on the way. The refetch alone is one request, and it is
 * right.
 */
function clearEveryUnreadCount(queryClient: QueryClient): void {
  getSession().setUnreadNotificationCount(0)
  void queryClient.invalidateQueries({ queryKey: [...qk.all, 'notifications'] })
  void queryClient.invalidateQueries({ queryKey: qk.workspaces() })
}
